// ── V121: 저장 상태 표시 ───────────────────────────────────────────────────────
// 예전엔 저장이 실패해도 화면에는 아무 표시가 없어서, 다음 날 열어 보고서야 사라진 걸 알았다.
// 헤더에 지금 저장 상태를 항상 보여준다.
//   저장 중…  → 서버로 보내는 중
//   ✓ 저장됨  → 방금 서버에 저장 완료 (몇 초 뒤 사라짐)
//   ⚠ 저장 안 됨 → 연결 문제 등으로 실패. 자동으로 다시 시도 중이며, 누르면 바로 다시 보낸다
// 중국 직원도 보므로 한중 병기.
// V147: 병기 대신 지금 화면 언어 하나로 (중국어 화면에 한국어가 섞이지 않게)
import React, { useEffect, useState } from 'react';
import { useLang } from '../i18n/LangContext';

const pill = (bg, color, border) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
  padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 800,
  background: bg, color, border: `1px solid ${border}`, whiteSpace: 'nowrap',
});

export default function SaveIndicator({ state, onRetry, compact }) {
  const { lang } = useLang();
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);
  const [, tick] = useState(0);
  const savedAt = state ? state.lastSavedAt : 0;
  const recent = !!savedAt && Date.now() - savedAt < 4000;
  useEffect(() => {
    if (!recent) return undefined;
    const t = setTimeout(() => tick(n => n + 1), Math.max(0, 4000 - (Date.now() - savedAt)) + 100);   // "저장됨"을 4초 뒤 감춘다
    return () => clearTimeout(t);
  }, [recent, savedAt]);

  if (!state) return null;
  if (state.failing) {
    return (
      <button type="button" onClick={onRetry}
        title={L(`저장 실패 (${state.lastError || '연결 오류'}) — 자동으로 다시 시도 중이에요. 누르면 바로 다시 보냅니다.`, `保存失败（${state.lastError || '网络错误'}）— 正在自动重试，点击立即重试`)}
        style={{ ...pill('#FEF2F2', '#B91C1C', '#FCA5A5'), cursor: 'pointer' }}>
        ⚠ {compact ? L('저장 안 됨', '未保存') : L('저장 안 됨 · 다시 시도', '未保存 · 重试')}
      </button>
    );
  }
  if (state.pending > 0) {
    return <span style={pill('#F4F4F5', '#52525B', '#E4E4E7')}>{L('저장 중…', '保存中…')}</span>;
  }
  if (recent) {
    return <span style={pill('#ECFDF5', '#047857', '#A7F3D0')}>✓ {L('저장됨', '已保存')}</span>;
  }
  return null;
}

// ── 지난번 못 보낸 내용 복원 배너 ─────────────────────────────────────────────
// 저장이 끝나기 전에 창을 닫았거나 연결이 끊겼을 때, 이 기기에 남겨 둔 내용을 다시 보낼지 묻는다.
const FIELD_LABELS = [
  [/^prices_/, '단가표', '单价表'],
  [/^cats_/, '레시피·품목', '配方·品目'],
  [/^orderHistory$/, '발주 기록', '订货记录'],
  [/^profitLoss$/, '손익', '损益'],
  [/^taxData$/, '세무', '税务'],
  [/^inventory$/, '재고', '库存'],
  [/^projects$/, '프로젝트', '项目'],
  [/^employees$/, '직원', '员工'],
  [/^schedules$/, '근무표', '排班'],
  [/^payrollSettings$/, '급여 설정', '工资设置'],
  [/^menuRecipeMap$/, '메뉴 연결', '菜单关联'],
];
const fieldLabel = (f, lang) => {
  const hit = FIELD_LABELS.find(([re]) => re.test(f));
  return hit ? (lang === 'zh' ? hit[2] : hit[1]) : f;
};
const whenLabel = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  const p = n => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export function UnsavedBanner({ backup, onRestore, onDiscard }) {
  const { lang } = useLang();
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);
  if (!backup || !backup.fields) return null;
  const labels = [...new Set(Object.keys(backup.fields).map(f => fieldLabel(f, lang)))].join(lang === 'zh' ? '、' : ', ');
  const btn = (bg, color, border) => ({
    padding: '5px 12px', borderRadius: 8, border: `1px solid ${border}`, background: bg, color,
    fontSize: 12, fontWeight: 800, cursor: 'pointer', flexShrink: 0,
  });
  return (
    <div style={{
      background: '#EFF6FF', borderBottom: '1px solid #93C5FD', padding: '9px 16px',
      fontSize: 12.5, color: '#1E3A8A', fontWeight: 600, lineHeight: 1.5,
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 8,
    }}>
      <span>
        💾 {L('서버에 저장되지 못한 내용이 이 기기에 남아 있어요', '本设备上有还没保存到服务器的内容')}
        <span style={{ fontWeight: 500, color: '#3B5BA9' }}> — {labels}{backup.savedAt ? ` · ${whenLabel(backup.savedAt)}` : ''}</span>
      </span>
      <button type="button" onClick={onRestore} style={btn('#1D4ED8', '#fff', '#1D4ED8')}>
        {L('복원해서 저장', '恢复并保存')}
      </button>
      <button type="button" style={btn('#fff', '#52525B', '#D4D4D8')}
        onClick={() => {
          if (window.confirm(L('남아 있는 내용을 버릴까요? 버리면 되돌릴 수 없어요.', '要放弃这些未保存的内容吗？放弃后无法恢复。'))) onDiscard();
        }}>
        {L('버리기', '放弃')}
      </button>
    </div>
  );
}
